import type { ImportReadProgress } from './geojson-stream.js';
import type { ImportWorkerOperations, ImportWorkerResponse, ImportWorkerTask, ImportWorkerValue } from './import-worker-operations.js';

type ImportWorkerRequest = { resolve(value: ImportWorkerValue): void; reject(error: Error): void; cleanup(): void; onProgress?: (progress: ImportReadProgress) => void };
type ActiveWorker = { status: 'running'; worker: Worker; pending: Map<number, ImportWorkerRequest> };
let state: { status: 'idle' } | ActiveWorker = { status: 'idle' };
let nextId = 0;

function stopWorker(active: ActiveWorker, error: Error): void {
  if (state !== active) return;
  state = { status: 'idle' };
  active.worker.terminate();
  for (const request of active.pending.values()) { request.cleanup(); request.reject(error); }
  active.pending.clear();
}

function getWorker(): ActiveWorker {
  if (state.status === 'running') return state;
  const current = new Worker(new URL('./import-worker.ts', import.meta.url), { type: 'module', name: 'map-import' });
  const active: ActiveWorker = { status: 'running', worker: current, pending: new Map() };
  const fail = () => {
    const error = new Error('The background import stopped. Retry the import.'); error.name = 'worker_error';
    stopWorker(active, error);
  };
  current.addEventListener('error', fail);
  current.addEventListener('messageerror', fail);
  current.addEventListener('message', (event: MessageEvent<ImportWorkerResponse>) => {
    if (state !== active) return;
    const response = event.data;
    const request = active.pending.get(response.id);
    if (!request) return;
    if ('progress' in response) { request.onProgress?.(response.progress); return; }
    active.pending.delete(response.id);
    request.cleanup();
    // Release the parsing heap after the last request. Blob handles and results
    // have already crossed the message boundary at this point.
    if (!active.pending.size) { current.terminate(); state = { status: 'idle' }; }
    if (response.ok) request.resolve(response.value);
    else { const error = new Error(response.error); error.name = response.code; request.reject(error); }
  });
  state = active;
  return active;
}

export function runImportWorker<K extends ImportWorkerTask>(task: K, input: Parameters<ImportWorkerOperations[K]>[0], signal?: AbortSignal, onProgress?: (progress: ImportReadProgress) => void): Promise<Awaited<ReturnType<ImportWorkerOperations[K]>>> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(new DOMException('Import cancelled.', 'AbortError')); return; }
    const current = getWorker();
    const id = ++nextId;
    const abort = () => {
      if (!current.pending.has(id)) return;
      stopWorker(current, new DOMException('Import cancelled.', 'AbortError'));
    };
    signal?.addEventListener('abort', abort, { once: true });
    const cleanup = () => signal?.removeEventListener('abort', abort);
    current.pending.set(id, {
      cleanup, onProgress,
      // SAFETY: Each request ID pairs this task with the result returned by its worker operation.
      resolve: (value) => resolve(value as Awaited<ReturnType<ImportWorkerOperations[K]>>), reject,
    });
    try { current.worker.postMessage({ id, task, input }); }
    catch (error) { cleanup(); current.pending.delete(id); if (!current.pending.size) stopWorker(current, new Error('Import could not start')); reject(error); }
  });
}
